import { useState, useEffect, useRef } from 'react';
import { internalRoomStatusesMap } from '../common/constants';
import { isCommingFromPcpOffice } from '../common/utility';
import useRoom from './useRoom';
import { room$, unknownGuests$, conversation$ } from '../services/subjects';
import useUnknownGuests from '../hooks/useUnknownGuests';

const INITIAL_PARTICIPANTS = [];
const INITIAL_HOST_PARTICIPANT = null;
const ROOM_EVENTS = {
    participantConnected: 'participantConnected',
    participantDisconnected: 'participantDisconnected',
    dominantSpeakerChanged: 'dominantSpeakerChanged',
    disconnected: 'disconnected',
};

export default function useParticipants(appointment, listenForDominantSpeakerChange = true) {
    const [participants, setParticipants] = useState(INITIAL_PARTICIPANTS);
    const [hostParticipant, setHostParticipant] = useState(INITIAL_HOST_PARTICIPANT);
    const hostParticipantRef = useRef(null);
    let { guests, count } = useUnknownGuests();
    const participantsRef = useRef(null);
    hostParticipantRef.current = hostParticipant;
    participantsRef.current = participants;
    const { room } = useRoom();

    function isHostCandidate(participant) {
        return isCommingFromPcpOffice(participant.identity);
    }

    function isUnknownGuest(identity) {
        if (identity === appointment.memberConnectionId || identity.startsWith(appointment.memberFullName)) {
            return false;
        }
        const attendee = appointment.optionalAttendees.find((optionalAttendee) => {
            if (optionalAttendee.connectionId === identity || identity.startsWith(optionalAttendee.fullName)) {
                return true;
            }
        });
        return !attendee;
    }

    function isHost(participant) {
        return participant.identity === hostParticipantRef.current?.identity;
    }

    function handleParticipantConnected(participant) {
        if (listenForDominantSpeakerChange) {
            if (isHostCandidate(participant) && !hostParticipantRef.current) {
                setHostParticipant(participant);
            } else if (isUnknownGuest(participant.identity)) {
                unknownGuests$.next([{ ...guests, [participant.identity]: `Guest ${++count}` }, count]);
            }
        }
        setParticipants((participants) => [...participants, participant]);
    }

    function handleParticipantDisconnected(participant) {
        if (isHost(participant)) {
            const newHost = participantsRef.current.find((p) => isHostCandidate(p) && !isHost(p));
            if (newHost) {
                setHostParticipant(newHost);
            } else {
                setHostParticipant(null);
            }
        }
        setParticipants((prevParticipants) => prevParticipants.filter((p) => p !== participant));
    }

    function handleDominantSpeakerChanged(participant) {
        if (!participant) {
            return;
        }
        if (isHostCandidate(participant)) {
            if (!isHost(participant)) {
                setHostParticipant(participant);
            }
        }
    }

    useEffect(() => {
        if (!room) {
            return;
        }

        const participants = Array.from(room.participants.values());
        const hostParticipant = participants.find((participant) => isHostCandidate(participant));
        setHostParticipant(hostParticipant);
        setParticipants([...participants, room.localParticipant]);

        room.on(ROOM_EVENTS.participantConnected, handleParticipantConnected).on(
            ROOM_EVENTS.participantDisconnected,
            handleParticipantDisconnected,
        );

        if (listenForDominantSpeakerChange) {
            room.on(ROOM_EVENTS.dominantSpeakerChanged, handleDominantSpeakerChanged);
        }

        room.on(ROOM_EVENTS.disconnected, () => {
            room$.next({ room: null, roomInternalStatus: internalRoomStatusesMap.DISCONNECTED });
            conversation$.next(null);
        });

        return () => {
            room.off(ROOM_EVENTS.participantConnected, handleParticipantConnected).off(
                ROOM_EVENTS.participantDisconnected,
                handleParticipantDisconnected,
            );

            if (listenForDominantSpeakerChange) {
                room.off(ROOM_EVENTS.dominantSpeakerChanged, handleDominantSpeakerChanged);
            }
        };
    }, [room, appointment]);

    return { hostParticipant, participants };
}
